import { useTypedSelector } from '@/store/reducers/use-typed-selector';
import { selectEditorNavV3 } from '@/store/selectors/feature-flags';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AppContext, { AppContexts } from 'components/app-context';
//https://brandlive-upload.s3-us-west-2.amazonaws.com/uploads/17/documents/drlc7nzefg/Screen_Shot_2020-11-02_at_8.52.48_AM.png this switch here

interface SwitchProps {
	value: any;
	on: boolean;
	onClick: (value: any, on: boolean) => void;
	inline?: boolean;
	disabled?: boolean;
	stopPropagation?: boolean;
	paused?: boolean;
	focusable?: boolean;
}

export default function Switch(props: SwitchProps): JSX.Element {
	const appContext = useContext(AppContext);
	const isAdmin = appContext === AppContexts.admin;
	const isEditorV3 = useTypedSelector(selectEditorNavV3);
	const switchV2Design = isEditorV3 && isAdmin;

	const [isFocused, setIsFocused] = useState(false);

	const switchRef = useRef<HTMLDivElement | null>(null);

	const disabled = !!props?.disabled;
	const focusable = props.focusable ?? true;
	const tabIndex = disabled ? -1 : 0;

	const memoizedClick = useCallback((e?: React.MouseEvent<HTMLDivElement>) => {
		if (disabled) return;
		props.stopPropagation && e?.stopPropagation();
		props.onClick(props.value, !props.on);
	}, [props, disabled]);

	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			if (disabled) return;
			// if spacebar, prevent scroll
			if (e.key === " " && isFocused) {
				e.preventDefault();
			}
			// if enter or spacebar, trigger click
			if ((e.key === 'Enter' || e.key === " ") && isFocused) {
				props.stopPropagation && e.stopPropagation();
				memoizedClick();
			}
		};
		document.addEventListener('keypress', handleKeyPress);
		return () => {
			document.removeEventListener('keypress', handleKeyPress);
		};
	}, [switchRef, isFocused, memoizedClick, disabled, props.stopPropagation]);

	return (
		<div
			tabIndex={focusable ? tabIndex : undefined}
			ref={switchRef}
			onFocus={() => {
				if (!disabled) {
					setIsFocused(true);
				}
			}}
			onBlur={() => setIsFocused(false)}
			className={classNames('form-control switch', { on: props.on, inlineSwitch: props.inline, isDisabled: disabled, paused: props.paused, v2: switchV2Design })}
		>
			<div className={classNames('gutter')} onClick={memoizedClick}>
				<div className="thumb" />
			</div>
		</div>
	);
}
